import React from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";

import { useTheme } from "@/hooks/useTheme";
import { PaymentGuarantee } from "@/containers/PaymentGuarantee";
import Oval from "@/icons/oval-copy-10.svg";
import { FlashSale } from "@/components/FlashSale";
import { useAuth } from "@/hooks/useAuth";
import { getFlashSaleEndDate } from "@/utils/getFlashSaleEndDate";
import { LogoProvider } from "@/components/LogoProvider";
import { AlreadyACustomer } from "@/components/AlreadyACustomer";
import { scrollTo } from "@/utils/scrollTo";
import { Heading } from "@/components/Heading";
import { usePricingContext } from "./state/context";
import { IPricing } from "./types";
import { SuccessModal } from "./SuccessModal";
import { PricesCurrencyText } from "./PricesCurrencyText";
import { PayAsYouGoCard, CreditsCard, SubscriptionCard } from "./Cards";
import { IPaymentType } from "../Converter/types";

type IProps = IPricing & JSX.IntrinsicElements["div"];

export const Pricing: React.FC<IProps> = ({
  title,
  subtitle,
  showGuarantee = false,
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const { send, current } = usePricingContext();
  const modalIsOpened = current.context.modalIsOpened;
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { push: redirect } = useRouter();
  const router = useRouter();

  React.useEffect(() => {
    if (
      (user?.email && !current.context.userEmail) ||
      (user?.email && current.context.userEmail !== user?.email)
    ) {
      send({ type: "SET_USER_EMAIL", email: user.email });
    }
  }, [user?.email, current.context.userEmail, send]);

  const onBuy = (planId: IPaymentType) => {
    send({
      type: "SELECT_PAYMENT_TYPE",
      paymentType: planId,
    });
  };

  return (
    <>
      <div
        className={css({
          position: "absolute",
          bottom: 0,
          left: 0,
          display: "none",
          [bp.FROM_LARGE_DESKTOP]: {
            display: "block",
          },
        })}
      >
        <Oval />
      </div>

      <div
        className={css({
          position: "relative",
          zIndex: 1,
        })}
      >
        <div
          className={css({
            textAlign: "center",
          })}
        >
          {router.pathname !== "/pricing" && (
            <Heading as="h2">{title || t("section/pricing:title")}</Heading>
          )}

          {subtitle && (
            <p
              className={css({
                fontSize: 18,
                letterSpacing: 0.5,
                color: colors.GRAY,
              })}
            >
              {subtitle}
            </p>
          )}
        </div>
        <FlashSale
          title="common:flashsale.title"
          subtitle={t("common:flashsale.description", {
            date: getFlashSaleEndDate("en-US", {
              day: "numeric",
              month: "long",
            }),
          })}
          tooltip={t("common:flashsale.tooltip")}
          onClick={() => {
            send({
              type: "SELECT_PAYMENT_TYPE",
              paymentType: "SUBSCRIPTION_UNLIMITED_ANNUAL",
            });
          }}
          customStyle={{ marginBottom: 32 }}
        />
        <AlreadyACustomer customStyle={{ marginBottom: 32 }} />
        <div
          className={css({
            marginTop: 40,
            [bp.FROM_TABLET]: {
              marginTop: 60,
            },
            [bp.FROM_DESKTOP]: {
              marginTop: 30,
            },
          })}
        >
          <PricesCurrencyText
            customStyle={{
              padding: "0 40px 15px",
              textAlign: "right",
              [bp.FROM_DESKTOP]: {
                padding: "0 30px 15px",
              },
            }}
          />
          <div
            className={css({
              display: "flex",
              justifyContent: "space-around",
              flexDirection: "column",
              [bp.FROM_DESKTOP]: { flexDirection: "row" },
              nested: {
                "> div:not(:first-of-type)": {
                  marginTop: 30,
                  [bp.FROM_TABLET]: {
                    marginTop: 40,
                  },
                  [bp.FROM_DESKTOP]: {
                    marginTop: 0,
                    marginLeft: 30,
                  },
                },
              },
            })}
          >
            <PayAsYouGoCard
              cta={{
                title: t("common:actions.doNow", {
                  do: t("common:actions.upload"),
                }),
                onClick: () => {
                  scrollTo("upload", () => redirect("/"));
                },
              }}
            />

            <CreditsCard
              cta={{
                title: t("common:actions.doNow", {
                  do: t("common:actions.pay"),
                }),
                onClick: onBuy,
              }}
            />

            <SubscriptionCard
              cta={{
                title: t("common:actions.doNow", {
                  do: t("common:actions.pay"),
                }),
                onClick: onBuy,
              }}
            />
          </div>
        </div>

        <div
          className={css({
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            [bp.FROM_DESKTOP]: {
              marginTop: 60,
              justifyContent: "space-around",
              flexWrap: "no-wrap",
            },
          })}
        >
          <LogoProvider title="PayPal" src="/svgs/paypal.svg" />
          <LogoProvider title="MasterCard" src="/svgs/mastercard.svg" />
          <LogoProvider title="VISA" src="/svgs/visa.svg" />
          <LogoProvider title="Amex" src="/svgs/amex.svg" />
          <LogoProvider title="Discover" src="/svgs/discover.svg" />
          <LogoProvider title="UnionPay" src="/svgs/unionpay.svg" />
        </div>
        {showGuarantee && <PaymentGuarantee />}
      </div>
      {modalIsOpened && <SuccessModal />}
    </>
  );
};

export type { IPricing };
